import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';

/**
 * Navigation state for a page that takes its own address without being another page: a new Ask
 * the data conversation once it has its first answer (APP-191 slice 3). Focus stays where the
 * person is, on the answer they were just given.
 */
export const KEEP_FOCUS = { keepFocus: true } as const;

function keepsFocus(state: unknown): boolean {
  return (
    typeof state === 'object' && state !== null && 'keepFocus' in state && state.keepFocus === true
  );
}

/**
 * After every move to another page, focus goes to that page's heading (QA-022, WCAG 2.4.3), so a
 * screen reader announces where the person landed instead of staying silent on <body>. The first
 * page load is left alone: the browser and the welcome screen own focus there. A change of query
 * or hash only (filters, tabs, drill-downs) is the same page, so focus stays where the person is.
 */
export function useRouteFocus(mainId = 'main'): void {
  const location = useLocation();
  const { pathname } = location;
  const previous = useRef(pathname);
  const stay = keepsFocus(location.state as unknown);
  useEffect(() => {
    if (previous.current === pathname) return;
    previous.current = pathname;
    if (stay) return;
    // After the new page has painted, so lazily loaded pages have their heading.
    const frame = requestAnimationFrame(() => {
      const main = document.getElementById(mainId);
      if (main === null) return;
      const heading = main.querySelector<HTMLElement>('h1');
      const target = heading ?? main;
      if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
      // The scroll position is the router's (ScrollRestoration): the top for a new page, the
      // remembered place on Back. Focusing mustn't move it.
      target.focus({ preventScroll: true });
    });
    return () => {
      cancelAnimationFrame(frame);
    };
  }, [pathname, mainId, stay]);
}
